import { FC, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { BottomNavigation, BottomNavigationAction, Box } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import hexToRgba from "hex-to-rgba";
import Dashboard from "./assets/Dashboard";
import Bot from "./assets/Bot";
import AddIconGeneral from "./assets/AddIconGeneral";
import Leader from "./assets/Leader";
import Profile from "./assets/Profile";
import NavBarMenu from "./components/NavBarMenu";
import { useWebApp } from "@vkruglikov/react-telegram-web-app";
import { useTranslation } from "react-i18next";
import Text from "../../_components/Text";

interface NavBarProps {
  disabledCreateButton: boolean;
}

const NavBar: FC<NavBarProps> = ({ disabledCreateButton }) => {
  const theme = useTheme();
  const location = useLocation();
  const WebApp = useWebApp();
  const { t } = useTranslation("navBar");

  const [showBotType, setShowBotType] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const createButtonRef = useRef<HTMLDivElement>(null);

  const getColor = useCallback(
    (path: string) =>
      location.pathname.startsWith(path)
        ? theme.palette.text.secondary
        : hexToRgba(theme.palette.text.primary, 0.5),
    [location.pathname, theme.palette]
  );

  const handleBotTypeMenu = useCallback(() => setShowBotType((prev) => !prev), []);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickOutside =
        !menuRef.current?.contains(target) && !createButtonRef.current?.contains(target);

      if (isClickOutside && showBotType) {
        setShowBotType(false);
      }
    },
    [showBotType]
  );

  const menuItems = useMemo(
    () => [
      {
        id: 2,
        to: "/dashboard",
        primary: t("dashboard"),
        renderIcon: (color: string) => <Dashboard colorCustom={color} size={32} />,
        disabled: true,
        soon: true,
      },
      {
        id: 1,
        to: "/bots",
        primary: t("bots"),
        renderIcon: (color: string) => <Bot colorCustom={color} size={32} />,
      },
      {
        id: 3,
        to: "",
        primary: t("create"),
        renderIcon: (color: string) => <AddIconGeneral colorCustom={color} size={74} />,
        disabled: disabledCreateButton,
        class: "MainAddButton",
        onClick: handleBotTypeMenu,
      },
      {
        id: 4,
        to: "/leaderboard",
        primary: t("leaderboard"),
        renderIcon: (color: string) => <Leader colorCustom={color} size={32} />,
        disabled: true,
        soon: true,
      },
      {
        id: 5,
        to: "/profile",
        primary: t("profile"),
        renderIcon: (color: string) => <Profile colorCustom={color} size={32} />,
      },
    ],
    [disabledCreateButton, handleBotTypeMenu]
  );

  const toggleMainContentClass = useCallback((show: boolean) => {
    const mainContent = document.querySelector(".main-content") as HTMLElement;
    if (mainContent) {
      mainContent.classList.toggle("ui-disabled-dark", show);
    }
  }, []);

  useEffect(() => {
    if (!showBotType) {
      document.removeEventListener("mousedown", handleClickOutside);
      toggleMainContentClass(false);
      return;
    }

    toggleMainContentClass(true);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      toggleMainContentClass(false);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showBotType, handleClickOutside, toggleMainContentClass]);

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      {showBotType && <NavBarMenu ref={menuRef} open={showBotType} />}
      <BottomNavigation
        className={`${WebApp?.platform === "android" || WebApp?.platform === "ios" ? "tg-mob-safe-area-inset-padding-bottom" : "tg-desktop-safe-area-inset-padding-bottom"}`}
        sx={{
          backgroundColor: theme.palette.mode === "dark" ? "black" : "rgba(241, 240, 246, 1)",
          paddingTop: "10px",
          paddingX: "8px",
          borderTop: "1px solid",
          borderColor:
            theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(21, 21, 21, 0.2)",
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          boxSizing: "content-box",
        }}
      >
        {menuItems.map(
          ({ id, to, primary, renderIcon, disabled, soon, onClick, class: className }) => (
            <Link
              key={id}
              className="NavigationBarLink"
              to={to || location.pathname}
              style={{
                maxWidth: "52px",
                pointerEvents: soon ? "none" : "auto",
              }}
            >
              <Box ref={!to ? createButtonRef : undefined}>
                <BottomNavigationAction
                  label={primary}
                  onClick={!disabled ? onClick : undefined}
                  icon={renderIcon(getColor(to))}
                  sx={{
                    padding: 0,
                    color: getColor(to),
                    borderTopLeftRadius: !to ? 50 : 12,
                    borderTopRightRadius: !to ? 50 : 12,
                    opacity: soon || disabled ? 0.5 : 1,
                  }}
                  className={
                    showBotType
                      ? `${className} AnimateCreateBotButton${theme.palette.mode === "light" ? "Light" : ""}`
                      : className
                  }
                />
              </Box>

              {soon && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translateX(-50%) translateY(-50%)",
                    backgroundColor: theme.myTheme.colors.secondary,
                    borderRadius: "12px",
                    padding: "2px 10px",
                    zIndex: 10,
                  }}
                >
                  <Text variant="s-semi" sx={{ opacity: "1" }}>
                    {t("soon")}
                  </Text>
                </Box>
              )}
            </Link>
          )
        )}
      </BottomNavigation>
    </Box>
  );
};

export default NavBar;
